
import { pdf } from '@react-pdf/renderer';
import { ResumeDocument } from '@/components/pdf/ResumeDocument';
import { CoverLetterDocument } from '@/components/pdf/CoverLetterDocument';

const NEXT_API = "/api";
// Keep Python API for non-migrated features (History)
const PYTHON_API = process.env.NEXT_PUBLIC_API_URL || "http://localhost:8000/api";

export async function uploadAndGenerate(
  file: File, 
  jobDesc: string, 
  rewriteMode: string,
  lockedSections: string[],
  onProgress: (data: any) => void,
  token: string
): Promise<any> {
    const formData = new FormData();
    formData.append("resume", file);
    formData.append("job_description", jobDesc);
    formData.append("rewrite_mode", rewriteMode);
    formData.append("locked_sections", JSON.stringify(lockedSections));

    // Progress updates with step and percent
    onProgress({ step: "Parsing Resume...", percent: 20 });

    // Start a progress simulator immediately
    let currentProgress = 20;
    const messages = [
        "Parsing Resume...",
        "Analyzing job requirements...",
        "Optimizing resume content...",
        "Tailoring experience section...",
        "Enhancing skills match...",
        "Generating cover letter...",
        "Finalizing documents..."
    ];
    
    const progressInterval = setInterval(() => {
        currentProgress = Math.min(currentProgress + 5, 90);
        const messageIndex = Math.floor(currentProgress / 15) % messages.length;
        onProgress({ step: messages[messageIndex], percent: currentProgress });
    }, 3000); // Update every 3 seconds

    try {
        const response = await fetch(`${NEXT_API}/generate`, {
            method: "POST",
            body: formData,
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });

        if (!response.ok) {
            clearInterval(progressInterval);
            const err = await response.json();
            throw new Error(err.error || "Generation failed");
        }
        
        const result = await response.json();
        
        // Clear the interval
        clearInterval(progressInterval);
        
        // Invalidate history cache so dashboard updates
        if (typeof window !== 'undefined') {
            sessionStorage.removeItem("resume_history_cache");
        }

        onProgress({ step: "Done!", percent: 100 });
        
        return result;
    } catch (e: any) {
        clearInterval(progressInterval);
        throw new Error(e.message);
    }
}

export async function renderPdf(data: any, type: "resume" | "cover_letter") {
    // Client-side PDF Generation for real-time preview!
    
    if (type === "resume") {
        // ResultsView passes { resume: ..., cover_letter: ... }
        // We need to extract the resume object specifically
        const resumeData = data.resume || data; 
        const blob = await pdf(<ResumeDocument data={resumeData} />).toBlob();
        return blob;
    }
    
    if (type === "cover_letter") {
         // data might be the full object, or just the text string depending on how it's called
         // ResultsView usually passes { resume: ..., cover_letter: "Text" ... }
         // We extract the text.
         const coverLetterText = data.cover_letter || data;
         
         const blob = await pdf(<CoverLetterDocument data={coverLetterText} />).toBlob();
         return blob;
    }
    
    throw new Error("Unknown PDF type");
}

// Simple in-memory cache for dashboard quick loads
const HISTORY_CACHE_KEY = "resume_history_cache";

export async function fetchHistory(page: number, limit: number, token: string, useCache: boolean = false) {
    if (useCache) {
        const cached = sessionStorage.getItem(HISTORY_CACHE_KEY);
        if (cached) {
            try {
                const parsed = JSON.parse(cached);
                // Optional: Check timestamp validity? for now simple key is fine.
                console.log("Using cached history");
                return parsed;
            } catch (e) {
                sessionStorage.removeItem(HISTORY_CACHE_KEY);
            }
        }
    }

    const response = await fetch(`${NEXT_API}/history?page=${page}&limit=${limit}`, {
        method: "GET",
        headers: {
             'Authorization': `Bearer ${token}`
        }
    });
    
    if (!response.ok) throw new Error("Failed to fetch history");
    
    const data = await response.json();
    
    // Only cache the first page (Dashboard view)
    if (useCache && page === 1) {
        sessionStorage.setItem(HISTORY_CACHE_KEY, JSON.stringify(data));
    }
    
    return data;
}

export async function fetchDocument(id: string, token: string) {
    const response = await fetch(`${NEXT_API}/documents/${id}`, {
        method: "GET",
        headers: {
            'Authorization': `Bearer ${token}`
        }
    });
    
    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch document");
    }
    
    return await response.json();
}
